import React, { useMemo } from "react";
import "../assets/css/occasion.css";

const JOURNEY_STEP_META = {
  arrival: {
    icon: "bi-box-arrow-in-right",
    accent: "is-arrival",
    short: "Arrival",
  },
  pickup: { icon: "bi-car-front-fill", accent: "is-pickup", short: "Pickup" },
  stay: { icon: "bi-house-door-fill", accent: "is-stay", short: "Stay" },
  occasion: {
    icon: "bi-calendar-heart-fill",
    accent: "is-occasion",
    short: "Event",
  },
  local: { icon: "bi-car-front-fill", accent: "is-local", short: "Local" },
  drop: { icon: "bi-box-arrow-right", accent: "is-drop", short: "Drop-off" },
  departure: {
    icon: "bi-box-arrow-up-right",
    accent: "is-departure",
    short: "Departure",
  },
};

/** Chronological journey order under the event card. */
const FLOW_TYPES = [
  "arrival",
  "pickup",
  "stay",
  "local",
  "drop",
  "departure",
];

const formatDisplayTime = (time) => {
  const value = String(time || "").trim();
  if (!value) return "";
  const match = value.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return value;
  const hours = Number(match[1]);
  const minutes = match[2];
  const period = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes} ${period}`;
};

const formatJourneyWhen = (date, time) => {
  const parts = [];
  if (date) {
    const parsed = new Date(date);
    parts.push(
      Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString()
    );
  }
  const prettyTime = formatDisplayTime(time);
  if (prettyTime) parts.push(prettyTime);
  return parts.join(" · ");
};

const formatPersonName = (person = {}) =>
  `${person.prefix || ""} ${person.firstName || ""} ${
    person.MiddleName || person.middleName || ""
  } ${person.lastName || ""} ${person.suffix || ""}`
    .replace(/\s+/g, " ")
    .trim();

const buildStaySteps = (stays = []) =>
  stays.map((stay) => ({
    type: "stay",
    title: "Stay",
    date: (stay.checkInDate || "").split("T")[0],
    time: stay.checkInTime || "",
    fields: [
      { label: "Hotel", value: stay.hotelName },
      {
        label: "Room",
        value: (stay.roomNumbers || []).join(", ") || stay.roomType,
      },
      {
        label: "Check-in",
        value: [
          stay.checkInDate?.split("T")[0] || stay.checkInDate,
          stay.checkInTime,
        ]
          .filter(Boolean)
          .join(" · "),
      },
      { label: "Status", value: stay.stayStatus },
    ].filter((field) => String(field.value || "").trim()),
  }));

const buildFlowModel = (timeline, stays) => {
  const steps = timeline.length > 0 ? timeline : buildStaySteps(stays);
  const occasions = steps.filter((step) => step.type === "occasion");
  const flowSteps = FLOW_TYPES.map((type) =>
    steps.find((item) => item.type === type)
  )
    .filter(Boolean)
    .map((step, index) => ({
      ...step,
      flowIndex: index,
    }));

  return {
    occasions,
    flowSteps,
    flowCount: flowSteps.length,
  };
};

const JourneyFlowCard = ({ step, fieldLimit = 3 }) => {
  const meta = JOURNEY_STEP_META[step.type] || {
    icon: "bi-circle-fill",
    accent: "is-default",
    short: step.title,
  };
  const previewFields = (step.fields || []).slice(0, fieldLimit);
  const when = formatJourneyWhen(step.date, step.time);

  return (
    <article
      className={`guest-journey-flow-card ${meta.accent}`}
      aria-label={`${meta.short} details`}
    >
      <div className="guest-journey-flow-card-head">
        <i className={`bi ${meta.icon}`} aria-hidden="true" />
        <strong>{meta.short}</strong>
      </div>
      {when ? <span className="guest-journey-flow-when">{when}</span> : null}
      {previewFields.length ? (
        <ul className="guest-journey-flow-fields">
          {previewFields.map((field) => (
            <li key={`${step.type}-${field.label}`}>
              <em>{field.label}</em>
              <span>{field.value}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
};

const GuestJourneyHub = ({ timeline, stays, eventName }) => {
  const { occasions, flowSteps, flowCount } = useMemo(
    () => buildFlowModel(timeline, stays),
    [timeline, stays]
  );
  const fieldLimit = flowCount <= 2 ? 4 : 3;
  const densityClass =
    flowCount <= 0
      ? "is-flow-empty"
      : `is-flow-count-${Math.min(6, flowCount)}`;

  return (
    <div className={`guest-journey-hub guest-journey-map ${densityClass}`}>
      <div className="guest-journey-map-scroll">
        <div className="guest-journey-map-rail">
          <div className="guest-journey-map-event">
            <div className="guest-journey-map-event-card">
              <div className="guest-journey-map-event-head">
                <div className="guest-journey-map-event-icon" aria-hidden="true">
                  <i className="bi bi-calendar-heart-fill" />
                </div>
                <div className="guest-journey-map-event-titles">
                  <span className="guest-journey-map-event-kicker">Event</span>
                  <strong>{eventName || "Event"}</strong>
                </div>
              </div>
              {occasions.length ? (
                <div className="guest-journey-map-occasions">
                  {occasions.map((occ, index) => {
                    const when = formatJourneyWhen(occ.date, occ.time);
                    return (
                      <div
                        className="guest-journey-map-occasion"
                        key={`occ-${occ.title}-${index}`}
                      >
                        <strong>{occ.title}</strong>
                        {when ? <span>{when}</span> : null}
                        {(occ.fields || []).slice(0, 1).map((field) => (
                          <em key={field.label}>{field.value}</em>
                        ))}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="guest-journey-map-event-empty">
                  {flowCount > 0
                    ? "Guest logistics continue below."
                    : "Occasions appear here when linked to stay or travel."}
                </p>
              )}
            </div>
          </div>

          {flowCount > 0 ? (
            <>
              <div className="guest-journey-map-bridge" aria-hidden="true">
                <span className="guest-journey-map-bridge-line" />
              </div>
              <ol className="guest-journey-map-steps">
                {flowSteps.map((step, index) => {
                  const isLast = index === flowSteps.length - 1;
                  const sideClass =
                    index % 2 === 0 ? "is-side-left" : "is-side-right";
                  return (
                    <li
                      key={`${step.type}-${step.flowIndex}`}
                      className={`guest-journey-map-step ${sideClass}${
                        isLast ? " is-last" : ""
                      }`}
                    >
                      <div
                        className="guest-journey-map-marker"
                        aria-hidden="true"
                      >
                        <span className="guest-journey-map-marker-line is-top" />
                        <span className="guest-journey-map-node-dot" />
                        <span className="guest-journey-map-marker-line is-bottom" />
                      </div>
                      <div className="guest-journey-map-step-body">
                        <JourneyFlowCard step={step} fieldLimit={fieldLimit} />
                      </div>
                    </li>
                  );
                })}
              </ol>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const GuestJourneyModal = ({
  isOpen,
  onClose,
  journeyData,
  guestSelected,
  eventName = "",
}) => {
  if (!isOpen) return null;

  const timeline = journeyData?.timeline || [];
  const stays = journeyData?.stay || [];
  const person = journeyData?.attendee || journeyData?.guest;
  const personName = person ? formatPersonName(person) : "Guest";

  return (
    <div
      className="guest-journey-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="guest-journey-modal-title"
      onClick={onClose}
    >
      <div
        className="guest-journey-modal-dialog guest-journey-modal-dialog-hub"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="guest-journey-modal-body travel-stay-page">
          {!guestSelected ? (
            <div className="guest-journey-modal-shell">
              <div className="guest-journey-modal-topbar">
                <button
                  type="button"
                  className="guest-journey-modal-close"
                  onClick={onClose}
                  aria-label="Close guest journey"
                >
                  ×
                </button>
              </div>
              <p className="guest-journey-modal-empty">
                Select a main guest to view their full event journey.
              </p>
            </div>
          ) : (
            <>
              <div className="guest-journey-modal-topbar">
                <div>
                  <strong id="guest-journey-modal-title">{personName}</strong>
                  <span>Event journey flow</span>
                </div>
                <button
                  type="button"
                  className="guest-journey-modal-close"
                  onClick={onClose}
                  aria-label="Close guest journey"
                >
                  ×
                </button>
              </div>

              <GuestJourneyHub
                timeline={timeline}
                stays={stays}
                eventName={eventName}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestJourneyModal;
